import cx from 'classnames';
import {
  useRef,
  useEffect,
  useContext,
  type Dispatch,
  type SetStateAction,
} from 'react';

import {
  useAppDispatch,
  useAppSelector,
  isEditingSelector,
  isEditingSettingsSelector,
  editedSpaceIdSelector,
  setEditedSpaceId,
  setMarkerEditingSpaceId,
  someSpaceHasImageSelector,
  showRouteSidebarSelector,
  preferencesSelector,
} from '../../store';
import { LeafletContext } from '../../App';

import { GroupedSpacesList } from '../spaces/Sidebar/View/GroupedSpacesList';
import Featured from './Featured';
import { EditSpace } from '../spaces/Sidebar/Edit/EditSpace';
import { EditSettings } from './EditSettings';
import { RouteSidebar } from '../wayfinding/RouteSidebar';

import './Sidebar.scss';

export interface SidebarButtonProps {
  isSidebarOpen: boolean;
  setIsSidebarOpen: Dispatch<SetStateAction<boolean>>;
}

export function SidebarButton({ isSidebarOpen, setIsSidebarOpen }: SidebarButtonProps) {
  return (
    <button
      className={'sidebar-button'}
      onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      aria-label="Toggle sidebar"
    />
  );
}

function SidebarContent() {
  const dispatch = useAppDispatch();
  const showRouteSidebar = useAppSelector(showRouteSidebarSelector);
  const isEditingSettings = useAppSelector(isEditingSettingsSelector);
  const editedSpaceId = useAppSelector(editedSpaceIdSelector);

  if (showRouteSidebar) {
    return (
      <RouteSidebar />
    );
  }

  if (isEditingSettings) {
    return (
      <EditSettings />
    );
  }

  if (editedSpaceId) {
    return (
      <EditSpace
        spaceId={editedSpaceId}
        onClose={() => {
          dispatch(setEditedSpaceId(null));
          dispatch(setMarkerEditingSpaceId(null));
        }}
      />
    );
  }

  return (
    <>
      <Featured />
      <section>
        <GroupedSpacesList />
      </section>
    </>
  );
}

export interface SidebarProps {
  isSidebarOpen: boolean;
}

export function Sidebar({
  isSidebarOpen,
}: SidebarProps) {
  const { mapRef } = useContext(LeafletContext);
  const isEditing = useAppSelector(isEditingSelector);
  const someSpaceHasImage = useAppSelector(someSpaceHasImageSelector);
  const preferences = useAppSelector(preferencesSelector);
  const isRight = preferences.sidebarPosition === 'right';
  const marginProp = isRight ? 'margin-right' : 'margin-left';

  const asideRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const aside = asideRef.current;
    if (!aside) return;

    // Removes transition effect so resizing window doesn't move the map
    const removeTransitions = (e: TransitionEvent) => {
      const element = e.target! as HTMLElement;
      if (element.classList.contains('sidebar-closing')) {
        element.classList.remove('sidebar-closing');
        element.classList.add('sidebar-closed');
      } else {
        element.classList.remove('sidebar-closed');
      }
      mapRef.current!.invalidateSize({
        animate: (e.propertyName === 'max-height'),
      });
    };

    // Pans the map when opening/closing Sidebar
    const panMap = (e: TransitionEvent) => {
      if (e.propertyName !== marginProp) return;

      const element = e.target! as HTMLElement;
      const duration = parseFloat(getComputedStyle(element).transitionDuration);
      const sign = isSidebarOpen ? 1 : -1;
      const offset: [number, number] = [(sign * element.offsetWidth) / 2, 0];

      mapRef.current!.panBy(offset, {
        animate: true,
        duration,
        easeLinearity: 0.5,
      });
    };

    const resizeObserver = new ResizeObserver(() => {
      mapRef.current?.whenReady(() => {
        mapRef.current!.invalidateSize();
      });
    });
    resizeObserver.observe(aside);

    aside.addEventListener('transitionstart', panMap);
    aside.addEventListener('transitionend', removeTransitions);
    return () => {
      aside.removeEventListener('transitionstart', panMap);
      aside.removeEventListener('transitionend', removeTransitions);
      resizeObserver.unobserve(aside);
    };
  }, [isSidebarOpen, isRight, marginProp, mapRef]);

  return (
    <aside
      ref={asideRef}
      className={cx('sidebar', {
        'sidebar-closing': !isSidebarOpen,
        editing: isEditing,
        'no-images': !someSpaceHasImage,
      })}
    >
      <nav>
        <SidebarContent />
      </nav>
    </aside>
  );
}
